import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Plus, Sparkles, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import { ActionButton, Button, IconButton } from '../../components/UI';

interface FaqItem {
  _id: string;
  question: string;
  answer: string;
  i18n?: I18nTriple;
  order: number;
  isActive: boolean;
}

const AdminFAQ: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FaqItem | null>(null);
  const { isExpanded: isModalExpanded, setIsExpanded: setIsModalExpanded, toggleExpanded: toggleModalExpanded } = useExpandable();
  useEscapeKey(() => closeModal(), modalOpen);
  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [order, setOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchFaq = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/faq/all');
      setItems(res.data);
    } catch (e) {
      console.error(e);
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaq();
  }, []);

  const openNew = () => {
    setEditing(null);
    setI18n(emptyI18nTriple());
    setOrder(items.length);
    setIsActive(true);
    setTopic('');
    setModalOpen(true);
  };

  const openEdit = (item: FaqItem) => {
    setEditing(item);
    setI18n({
      pt: {
        question: item.i18n?.pt?.question || item.question,
        answer: item.i18n?.pt?.answer || item.answer
      },
      en: {
        question: item.i18n?.en?.question || '',
        answer: item.i18n?.en?.answer || ''
      },
      es: {
        question: item.i18n?.es?.question || '',
        answer: item.i18n?.es?.answer || ''
      }
    });
    setOrder(item.order);
    setIsActive(item.isActive);
    setTopic('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setIsModalExpanded(false);
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      notifications.warning.validation('Informe o tema antes de gerar a pergunta.');
      return;
    }
    try {
      setGenerating(true);
      const response = await axios.post('/faq/generate', { topic });
      const { question, answer } = response.data;
      setI18n((prev) => ({
        pt: { ...prev.pt, question: question?.pt || '', answer: answer?.pt || '' },
        en: { ...prev.en, question: question?.en || '', answer: answer?.en || '' },
        es: { ...prev.es, question: question?.es || '', answer: answer?.es || '' }
      }));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.error.socialPostGenerate(error.response?.data?.message || 'Erro ao gerar pergunta e resposta');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ptQ = i18n.pt?.question?.trim();
    const ptA = i18n.pt?.answer?.trim();
    if (!ptQ || !ptA) return;
    try {
      setSaving(true);
      const payload = {
        question: ptQ,
        answer: ptA,
        i18n,
        order,
        isActive
      };
      if (editing) {
        await axios.put(`/faq/${editing._id}`, payload);
      } else {
        await axios.post('/faq', payload);
      }
      notifications.success.settingsUpdated();
      closeModal();
      fetchFaq();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: t('admin.faqDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/faq/${id}`);
      notifications.success.settingsUpdated();
      fetchFaq();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(error.response?.data?.message || t('common.error'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <Breadcrumbs />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.faq')}</h1>
            <p className="text-gray-600">{t('admin.faqPageDesc')}</p>
          </div>
          <Button type="button" onClick={openNew}>
            <Plus className="w-4 h-4" />
            {t('admin.faqNew')}
          </Button>
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="mt-8 card text-center py-12 text-gray-500">{t('admin.faqEmpty')}</div>
        ) : (
          <div className="mt-6 space-y-3">
            {items.map((item) => (
              <div
                key={item._id}
                className="card flex flex-col md:flex-row md:items-start md:justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <HelpCircle className="w-4 h-4 text-primary-600 shrink-0" />
                    <span className="font-medium text-gray-900">{item.question}</span>
                    {!item.isActive && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                        {t('admin.inactive')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{item.answer}</p>
                </div>
                <div className="row-actions md:shrink-0">
                  <ActionButton
                    action="edit"
                    onClick={() => openEdit(item)}
                  />
                  <ActionButton
                    action="delete"
                    onClick={() => handleDelete(item._id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-2xl max-h-[90vh]'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editing ? t('admin.faqEdit') : t('admin.faqNew')}
              </h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleModalExpanded}
                  className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <IconButton action="close" onClick={closeModal} />
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tema (para gerar com IA)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Ex: Documentos necessários para pedir cidadania"
                    className="field flex-1"
                  />
                  <Button className="whitespace-nowrap"
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating}>
                    <Sparkles className="w-4 h-4" />
                    {generating ? 'Gerando...' : 'Gerar com IA'}
                  </Button>
                </div>
              </div>
              <I18nTripleFields mode="faq" value={i18n} onChange={setI18n} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.order')}</label>
                  <input
                    type="number"
                    min={0}
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
                    className="field"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    {t('admin.active')}
                  </label>
                </div>
              </div>
              <div className="form-actions pt-2">
                <Button variant="ghost" size="sm"
                  type="button"
                  onClick={closeModal}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? t('common.sending') : t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFAQ;
